import React, { useCallback, useEffect, useMemo, useState } from "react";
import { styled } from "styled-components";
import {
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  getExpandedRowModel,
  getSortedRowModel,
  useReactTable,
  type ColumnSizingState,
  type ExpandedState,
  type SortingState,
  type VisibilityState,
} from "@tanstack/react-table";
import { ConfigSchema } from "./configTypes";
import { formatAttr } from "./configModel";
import { ColumnSpec, planColumns } from "./tableLayout";
import {
  capabilityFields,
  capabilityLadder,
  ladderHeight,
  rungLabel,
} from "./capability";
import CapabilityCell from "./CapabilityCell";
import RowDetail from "./RowDetail";
import {
  NameMode,
  OP_CONFIG,
  opBreakdown,
  OpStatus,
  primaryName,
  unclassifiedCount,
  Vehicle,
} from "./data";
import StateBadge from "./StateBadge";
import ReadinessBadge, { UNSET } from "./ReadinessBadge";
import ValueChip from "./ValueChip";
import { tokens } from "./tokens";
import {
  HoverCard,
  HoverCardContent,
  HoverCardTrigger,
} from "../../components/ui/hover-card";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "../../components/ui/tooltip";

/**
 * The fleet, at three depths.
 *
 * The table used to put all 19 config fields on screen at once and scroll
 * sideways to fit them. That is a spreadsheet, not a console: the pinned name
 * survived the scroll but State and Open tickets — the two columns everything
 * else is judged against — slid away, so the evidence and the verdict could not
 * be read together. It also flattened the record, giving a lidar calibration
 * date the same standing as an autonomy approval.
 *
 * So the fields are ranked instead of listed, across three levels:
 *
 * 1. **The row** — identity, state, capability, open tickets, and the two
 *    assignment/hardware facts that decide what a tractor is for today. Sized to
 *    fit a laptop without horizontal scrolling.
 * 2. **The expanded row** (`RowDetail`) — the rest of the record, in place, for
 *    comparing tractors without losing your place.
 * 3. **The tractor page** — the full record, editing, history, tickets, map.
 *
 * Two things make the demotions safe rather than lossy. The four capability
 * approvals collapse into one ladder column instead of being dropped (see
 * `capability.ts`), and a field that is quiet most of the time but urgent
 * occasionally — a stale lidar calibration — loses its column but keeps its
 * exception, as the stale badge beside it in the expansion and on the tractor
 * page. A column earns its place by what it looks like when it is *wrong*, not
 * by how often it is read.
 *
 * The header is a single row now. Grouped headers earned their keep over 14
 * config columns spanning six groups; over two they were an empty band.
 * Grouping still exists where it is useful — in the expansion.
 *
 * ## How it sizes itself
 *
 * Columns carry explicit sizes under `table-layout: fixed`, so a column is the
 * width it is regardless of what the rows contain — the automatic algorithm
 * re-derives widths from the currently rendered rows, which made them jump when
 * the fleet was filtered. Which columns, and how wide, is a function of the
 * scroll box's measured width (`tableLayout.planColumns`, fed by a
 * ResizeObserver): the four core columns are always there; more schema fields
 * earn a column as the width allows, in priority order; and whatever is left
 * is shared across the columns by weight, so the row fills the box at a laptop
 * and at 3440px alike instead of a trailing spacer holding two thirds of every
 * row. Narrower than the columns' minimums it scrolls inside its own box.
 * Height is `flex: 1` in FleetView's column, so the box is exactly what the
 * masthead left over, at any viewport.
 *
 * ## What may truncate, and what may not
 *
 * A cell's own value never ellipses: a status column that reads "Decommissi…"
 * is a different status, and a name column that reads "AV-DL…" is no tractor.
 * So the columns whose vocabulary is known are sized from it at runtime — the
 * widest state label, the widest allocation label, the widest name in the fleet
 * — and the defaults below are only what jsdom and a fleet without a schema fall
 * back to. Secondary markers do not share a cell with a value they could push
 * out: a stale count used to sit beside the name and won the fight for width
 * ("AV-DL… 2 STALE"). Staleness now lives where it is acted on — as the badge
 * on each field in the expansion and on the tractor page — and not on the row.
 * What the reader drags afterwards still wins over all of this.
 *
 * ## Keyboard
 *
 * The tractor's name is a real link, and it is the row's one tab stop for
 * opening the tractor — the row itself used to be focusable too, which put an
 * unnamed "row" in the tab order and drew its focus ring underneath the pinned
 * name cell. The column separators are draggable but not tabbable; a column is
 * resized from its header with Shift + ←/→, so the header row costs one stop per
 * column rather than two.
 */
interface VehicleTableProps {
  vehicles: Vehicle[];
  schema: ConfigSchema | null;
  nameMode: NameMode;
  onOpen: (vehicleName: string) => void;
  /** The search the rows were narrowed by, so the empty state can name it. */
  query?: string;
  onClearQuery?: () => void;
  /**
   * The fleet to size the name column from — the whole fleet, not the filtered
   * rows, or the column would shrink and grow as the search narrowed it.
   */
  sizeNamesFrom?: Vehicle[];
}

const columnHelper = createColumnHelper<Vehicle>();

/**
 * The config fields that may earn a column, in display order.
 *
 * Everything else in the schema falls through to the expansion, which is the
 * safe default: a field added to `tractor_config.py` shows up in the record
 * without anyone having decided it belongs in a fleet-wide scan.
 *
 * Which of these are actually shown depends on the width the table has. The
 * four core columns (tractor, state, capability, tickets) are always there;
 * these are promoted by `priority` while they fit at their minimum width — the
 * two `defaultVisible` ones are what a laptop gets, and what renders before the
 * box has been measured. A 1440px display adds run type; a 2560px one adds the
 * lidar date and the location, and the rest of the width is shared out rather
 * than parked. A field that has no column falls back to the expansion, which
 * reads the visible set and shows the remainder (see RowDetail's
 * `columnFields`).
 *
 * `run_type` used to be cut outright to fit a laptop without truncation — at
 * laptop width it still is. `size` is the fallback when nothing can be measured
 * (jsdom); in a browser an enum is sized from its widest option label, a string
 * or date from its widest value in the fleet, and every column from its own
 * header, since a truncated *heading* is worse than a truncated value.
 *
 * What the reader drags afterwards still wins over all of this: a dragged
 * column is pinned at that width and the others share what is left.
 */
interface ColumnField {
  name: string;
  /** Fallback minimum, used where text cannot be measured. */
  size: number;
  /** Lower is promoted first as the width grows. */
  priority: number;
  /** Shown at laptop width and before the box is measured. */
  defaultVisible?: boolean;
  /** A shorter heading than the schema label, for the header row only. */
  header?: string;
}

const COLUMN_FIELDS: readonly ColumnField[] = [
  { name: "allocation", size: 140, priority: 1, defaultVisible: true },
  { name: "hardware_version", size: 130, priority: 2, defaultVisible: true },
  { name: "run_type", size: 200, priority: 3 },
  {
    name: "lidar_calibration_date",
    size: 170,
    priority: 4,
    header: "Lidar calibration",
  },
  { name: "location", size: 110, priority: 5 },
];

/**
 * How eagerly each core column takes leftover width. The ticket cell holds a
 * count plus severity chips and the capability cell a label plus pips — both
 * read better with room — while a name or a state chip gains little. Config
 * columns default to 1.
 */
const GROW: Record<string, number> = {
  name: 1,
  operationalState: 0.8,
  production: 0.5,
  capability: 1.5,
  openTicketCount: 2,
};

/**
 * Sort rank for the Production column: ready first, then the two reds, then
 * the unmarked. A value the vocabulary does not know sorts last rather than
 * throwing, like `attention_rank` on the backend.
 */
const READINESS_RANK: Record<string, number> = {
  READY: 0,
  BLOCKED: 1,
  NOT_READY: 2,
  [UNSET]: 3,
};

/** Fallback widths for the columns that are otherwise measured. */
const NAME_SIZE = 165;
const STATE_SIZE = 130;
const PRODUCTION_SIZE = 120;
const CAPABILITY_SIZE = 180;
const TICKETS_SIZE = 200;

/**
 * The widest of some strings in a font, or null where text cannot be measured
 * (jsdom has no canvas; a locked-down browser may refuse one).
 */
function widest(texts: string[], font: string): number | null {
  try {
    const ctx = document.createElement("canvas").getContext("2d");
    if (!ctx) return null;
    ctx.font = font;
    return texts.reduce((w, t) => Math.max(w, ctx.measureText(t).width), 0);
  } catch {
    return null;
  }
}

/**
 * A column width that fits its widest value.
 *
 * `extra` is the chrome around the text — cell padding, a chip's own padding
 * and border, a disclosure control — and the bounds keep one absurd label from
 * taking the table with it. Falls back to the static default where nothing can
 * be measured, which is also what keeps the tests deterministic.
 */
function sizeFor(
  texts: string[],
  font: string,
  extra: number,
  fallback: number,
  min: number,
  max: number,
): number {
  if (!texts.length) return fallback;
  const w = widest(texts, font);
  if (w === null) return fallback;
  return Math.round(Math.min(max, Math.max(min, w + extra)));
}

/** Cell padding (12px a side) — see `cell`. */
const CELL_PAD = 24;
/** A StateBadge / ValueChip's own horizontal padding and border. */
const CHIP_PAD = 18;

const MIN_SIZE = 80;
const MAX_SIZE = 900;

/** Keyboard resize step for the columns, and its coarse variant. */
const NUDGE = 12;
const NUDGE_COARSE = 40;

/**
 * Where the reader's pinned column widths live between visits.
 *
 * Versioned in the key so a future change to what a width *means* can't inherit
 * numbers written under the old meaning — v3 is exactly that change: an entry
 * is now an *override* the reader set (drag, Shift+arrows), not the column's
 * width, and columns without one share the box (see tableLayout). Entries are
 * stored per column id and never pruned: the schema arrives asynchronously, so
 * a width whose column is "missing" at load time is usually a column that
 * simply hasn't loaded yet, and dropping it would silently discard the reader's
 * sizing on every cold start. Unknown ids are inert.
 */
const WIDTHS_KEY = "tractorState.vehicleTable.columnWidths.v3";

function loadWidths(): ColumnSizingState {
  try {
    const raw = window.localStorage.getItem(WIDTHS_KEY);
    const parsed: unknown = raw ? JSON.parse(raw) : null;
    if (!parsed || typeof parsed !== "object") return {};
    const widths: ColumnSizingState = {};
    for (const [id, value] of Object.entries(parsed)) {
      // Anything non-numeric would be applied by TanStack as-is and produce an
      // unusable column, so the stored blob is validated rather than trusted.
      if (typeof value === "number" && Number.isFinite(value)) {
        widths[id] = Math.min(MAX_SIZE, Math.max(MIN_SIZE, value));
      }
    }
    return widths;
  } catch {
    // Unparseable or unavailable (private mode). Fall back to the defaults.
    return {};
  }
}

/** Matches the old card treatment for a status we have no colour for. */
const NEUTRAL_OP = {
  bg: tokens.offWhite,
  color: tokens.slate,
  border: tokens.line,
};

/**
 * What a severity is called in the cell. Short, but never a bare hue: "2" on an
 * orange wash and "1" on a yellow one were the only difference between MAJOR
 * and MINOR, which is no difference at all to a colour-blind reader or a
 * screen reader. "maj"/"min" were tried and confused — one letter apart.
 */
const OP_SHORT: Record<string, string> = {
  CRITICAL: "crit",
  MAJOR: "major",
  MINOR: "minor",
};

const opShort = (op: string) => OP_SHORT[op] ?? op.toLowerCase();

/**
 * What the Open-tickets cell would measure for one tractor, or null where text
 * cannot be measured.
 *
 * The cell has a hierarchy: the total always; the severity chips always — they
 * are the column's reason to exist; the unrated remainder last, and *that* is
 * the part that gives way. `full` measures it with its word ("+124 unrated");
 * without, it is the bare "+124" the cell falls back to when the column cannot
 * hold the sentence. Degrading the least important part on purpose beats an
 * ellipsis landing wherever the width happened to run out ("+124 ur…").
 */
function ticketCellWidth(v: Vehicle, full: boolean): number | null {
  const total = v.openTicketCount;
  if (total === 0) return 20;
  const parts: { text: string; font: string; pad: number }[] = [
    { text: String(total), font: `700 13px ${tokens.fontSans}`, pad: 0 },
  ];
  for (const { op, count } of opBreakdown(v)) {
    parts.push({
      text: `${count} ${opShort(op)}`,
      font: `600 11px ${tokens.fontSans}`,
      pad: 12,
    });
  }
  const unrated = unclassifiedCount(v);
  if (unrated > 0) {
    parts.push({
      text:
        unrated === total
          ? "· unrated"
          : full
            ? `+${unrated} unrated`
            : `+${unrated}`,
      font: `11.5px ${tokens.fontSans}`,
      pad: 0,
    });
  }
  let width = 0;
  for (const part of parts) {
    const w = widest([part.text], part.font);
    if (w === null) return null;
    width += w + part.pad;
  }
  return width + 6 * (parts.length - 1);
}

/**
 * Hover-card timing, shared by every card in the table. Quicker than Radix's
 * defaults (700ms/300ms): these cards preview the row's own detail rather than
 * somewhere else, so the intent bar is lower — but not so quick that sweeping
 * the cursor down the fleet opens a card per row crossed.
 */
const CARD_OPEN_DELAY = 150;
const CARD_CLOSE_DELAY = 80;

/**
 * Columns whose cells carry their own hover surface (a Radix card or tooltip).
 * They skip the generic `title` fallback the other cells get for truncation —
 * which for these two was worse than nothing anyway: their accessors return
 * sort keys, so the native tooltip said "112" over the ticket chips and "3"
 * over the capability ladder.
 */
const HOVER_OWNED_COLUMNS = new Set(["capability", "openTicketCount"]);

/**
 * Row backgrounds. Opaque, and deliberately not `offWhite`.
 *
 * Two bugs live in the obvious version of this.
 *
 * **Translucency.** The pinned first column carries `background: inherit`, which
 * copies the row's *computed* colour and paints it again on top. With an
 * `rgba()` row that double-paints and the name column comes out a shade darker
 * than the rest of its own row — one row rendered in two colours. Opaque values
 * paint identically however many times they land.
 *
 * **`offWhite` is already spoken for.** It is the fill of the neutral value chip
 * and of the chip an unknown ticket status gets, so using it as the hover made
 * those chips dissolve into the row exactly when the cursor was on them. Hover
 * is a faint blue instead: nothing in a cell is blue, so nothing can disappear
 * into it.
 *
 * Blue means "you are pointing at this, or it is open" throughout — which is why
 * the capability rail is *green* rather than blue. Reaching a rung is a fact
 * about the tractor, not an interaction, and the green matches the ✓ chips
 * beside it.
 */
const ROW = {
  plain: tokens.white,
  band: "#F2F3F5",
  hover: "#F0F6FF",
  open: "#E6F0FF",
  openHover: "#DBE9FF",
};

const VehicleTable: React.FC<VehicleTableProps> = ({
  vehicles,
  schema,
  nameMode,
  onOpen,
  query,
  onClearQuery,
  sizeNamesFrom,
}) => {
  const [sorting, setSorting] = useState<SortingState>([]);
  const [expanded, setExpanded] = useState<ExpandedState>({});
  // Widths the reader has set by hand. Everything else is derived from the
  // box's measured width — see `layout` below.
  const [pinned, setPinned] = useState<ColumnSizingState>(loadWidths);

  useEffect(() => {
    try {
      window.localStorage.setItem(WIDTHS_KEY, JSON.stringify(pinned));
    } catch {
      // Quota or private mode: the widths just don't survive the reload.
      return;
    }
  }, [pinned]);

  /**
   * The scroll box's inner width, observed rather than read from the window:
   * the nav, the page padding and a vertical scrollbar all sit between the two,
   * and a split-screen arrangement changes one without the other. `null` until
   * measured (and always in jsdom, which has no ResizeObserver), which renders
   * the default column set at minimum widths.
   */
  const [scrollEl, setScrollEl] = useState<HTMLDivElement | null>(null);
  const [boxWidth, setBoxWidth] = useState<number | null>(null);
  useEffect(() => {
    if (!scrollEl || typeof ResizeObserver === "undefined") return;
    const apply = (w: number) => setBoxWidth(w > 0 ? Math.floor(w) : null);
    apply(scrollEl.clientWidth);
    const observer = new ResizeObserver((entries) => {
      const w = entries[0]?.contentRect?.width;
      apply(typeof w === "number" && w > 0 ? w : scrollEl.clientWidth);
    });
    observer.observe(scrollEl);
    return () => observer.disconnect();
  }, [scrollEl]);

  /**
   * Fixed at mount. Staleness is measured against it, and a clock that ticked
   * mid-render would let two rows disagree about what "today" is — and would
   * re-render the whole fleet each time it moved.
   */
  const now = useMemo(() => Date.now(), []);

  const columns = useMemo(() => {
    const byName = new Map((schema?.fields ?? []).map((f) => [f.name, f]));

    const identity = columnHelper.accessor((v) => primaryName(v, nameMode), {
      id: "name",
      header: "Tractor",
      // The name is the one thing in the row that must never ellipse, so the
      // column is as wide as the widest name in the fleet.
      size: sizeFor(
        (sizeNamesFrom ?? vehicles).map((v) => primaryName(v, nameMode)),
        `600 13px ${tokens.fontSans}`,
        CELL_PAD + 18 + 6 + 8,
        NAME_SIZE,
        140,
        280,
      ),
      cell: (info) => {
        const row = info.row;
        const open = row.getIsExpanded();
        const vehicleName = row.original.vehicleName;
        return (
          <NameInner>
            <Chevron
              type="button"
              aria-expanded={open}
              aria-controls={`row-detail-${vehicleName}`}
              aria-label={`${open ? "Collapse" : "Expand"} ${info.getValue()}`}
              // The row itself opens the tractor, so the disclosure control has
              // to keep its click to itself.
              onClick={(e) => {
                e.stopPropagation();
                row.toggleExpanded();
              }}
              onKeyDown={(e) => e.stopPropagation()}
            >
              <Caret $open={open} aria-hidden>
                ▸
              </Caret>
            </Chevron>
            {/* A real link: it has a name, a URL to copy, and a focus ring
                that isn't hidden under the pinned cell. */}
            <NameLink
              href={`/tractor-state/${encodeURIComponent(vehicleName)}`}
              onClick={(e) => {
                if (e.metaKey || e.ctrlKey || e.shiftKey || e.button !== 0) {
                  // Let the browser open a new tab when asked to.
                  e.stopPropagation();
                  return;
                }
                e.preventDefault();
                e.stopPropagation();
                onOpen(vehicleName);
              }}
            >
              {info.getValue()}
            </NameLink>
          </NameInner>
        );
      },
    });

    // Sized from the vocabulary: "Decommissioned" is the widest state label
    // today, and a new one arrives with its own width rather than an ellipsis.
    const state = columnHelper.accessor((v) => v.operationalState ?? "", {
      id: "operationalState",
      header: "State",
      size: sizeFor(
        (schema?.operational_states ?? []).map((s) => s.label),
        `600 11px ${tokens.fontSans}`,
        CELL_PAD + CHIP_PAD + 8,
        STATE_SIZE,
        100,
        220,
      ),
      cell: (info) => (
        <StateBadge
          state={info.row.original.operationalState}
          schema={schema}
          size="sm"
        />
      ),
    });

    // The second derived axis, beside the first. Present only when the API
    // serves the vocabulary: an older backend, or a vendor's schema, gets the
    // table it always had rather than a column of dashes.
    const readinessStates = schema?.production_readiness_states;
    const production = readinessStates
      ? columnHelper.accessor(
          (v) => READINESS_RANK[v.productionReadiness?.value ?? UNSET] ?? 9,
          {
            id: "production",
            header: "Production",
            // A number-valued column sorts descending first by default, which
            // would lead with the unmarked. The first click should answer
            // "which are ready": ascending rank, ready first.
            sortDescFirst: false,
            size: sizeFor(
              readinessStates.map((s) => s.label),
              `600 11px ${tokens.fontSans}`,
              CELL_PAD + CHIP_PAD + 8,
              PRODUCTION_SIZE,
              110,
              160,
            ),
            meta: {
              help: "Ready for production as a whole — the admin's mark, checked against PMI, servicing, offline and Jira blockers",
            },
            cell: (info) => {
              const readiness = info.row.original.productionReadiness;
              return readiness && readiness.value !== UNSET ? (
                <ReadinessBadge
                  readiness={readiness}
                  schema={schema}
                  size="sm"
                />
              ) : (
                <Unmarked aria-label="not marked">—</Unmarked>
              );
            },
          },
        )
      : null;

    // Sorts on ladder height, not on the rendered label: the point of the
    // column is "how far up", and sorting the text would order the fleet
    // alphabetically by rung name.
    const capability = columnHelper.accessor(
      (v) => ladderHeight(capabilityLadder(schema, v.attrs)),
      {
        id: "capability",
        header: "Capability",
        // The widest rung label, plus the pips, the glyph and the ragged
        // marker that can sit beside it — "Auto production" ellipsed at the
        // old fixed width, and a truncated rung is a different approval.
        size: sizeFor(
          capabilityFields(schema).map(rungLabel),
          `600 12px ${tokens.fontSans}`,
          CELL_PAD + 4 * 9 + 7 + 18 + 22 + 8,
          CAPABILITY_SIZE,
          140,
          260,
        ),
        meta: {
          help: "Highest approval held, of manual → shadow → autonomous testing → autonomous production",
        },
        cell: (info) => (
          <CapabilityCell
            ladder={capabilityLadder(schema, info.row.original.attrs)}
          />
        ),
      },
    );

    // Wide enough for every row's count and chips at least in their compact
    // form; the remainder's word is shown where it also fits (see the cell).
    const ticketsSize = (() => {
      const widths = (sizeNamesFrom ?? vehicles).map((v) =>
        ticketCellWidth(v, false),
      );
      if (!widths.length || widths.some((w) => w === null)) return TICKETS_SIZE;
      const w = Math.max(...(widths as number[])) + CELL_PAD + 8;
      return Math.round(Math.min(260, Math.max(160, w)));
    })();

    const tickets = columnHelper.accessor((v) => v.openTicketCount, {
      id: "openTicketCount",
      header: "Open tickets",
      size: ticketsSize,
      cell: (info) => {
        const vehicle = info.row.original;
        const total = info.getValue();
        if (total === 0) return <Quiet>0</Quiet>;

        const breakdown = opBreakdown(vehicle);
        const unrated = unclassifiedCount(vehicle);
        // Decided against the column's designed width, not its live one: the
        // body is deliberately not re-rendered on every drag, and a reader who
        // narrows the column has chosen the ellipsis.
        const fullWidth = ticketCellWidth(vehicle, true);
        const spelledOut =
          fullWidth === null ||
          fullWidth + CELL_PAD <= (info.column.columnDef.size ?? TICKETS_SIZE);
        return (
          <HoverCard openDelay={CARD_OPEN_DELAY} closeDelay={CARD_CLOSE_DELAY}>
            <HoverCardTrigger asChild>
              {/* A button, so the preview opens for a keyboard too. It does
                  what the row does — opens the tractor — so it adds a way in,
                  not a second behaviour. */}
              <TicketCell
                type="button"
                aria-label={`${total} open tickets — open ${primaryName(vehicle, nameMode)}`}
                onClick={(e) => {
                  e.stopPropagation();
                  onOpen(vehicle.vehicleName);
                }}
              >
                <TicketTotal>{total}</TicketTotal>
                {breakdown.map(({ op, count, known }) => {
                  const oc = known ? OP_CONFIG[op as OpStatus] : NEUTRAL_OP;
                  return (
                    <SeverityChip
                      key={op}
                      style={{
                        background: oc.bg,
                        color: oc.color,
                        borderColor: oc.border,
                      }}
                    >
                      {count} {opShort(op)}
                    </SeverityChip>
                  );
                })}
                {/* "21 +21" read as a double count when nothing was rated.
                    Say what the remainder is, and drop the plus when it is
                    the whole number. */}
                {unrated > 0 && (
                  <Unrated
                    title={`${unrated} with no operational status set in Jira`}
                  >
                    {unrated === total
                      ? "· unrated"
                      : spelledOut
                        ? `+${unrated} unrated`
                        : `+${unrated}`}
                  </Unrated>
                )}
              </TicketCell>
            </HoverCardTrigger>
            <HoverCardContent className="w-80">
              <TicketPreview vehicle={vehicle} />
            </HoverCardContent>
          </HoverCard>
        );
      },
    });

    const configColumns = COLUMN_FIELDS.flatMap(({ name, size, header }) => {
      const field = byName.get(name);
      // A field the schema has dropped simply loses its column, rather than
      // rendering a header over empty cells.
      if (!field) return [];
      const label = header ?? field.label;
      // An enum's widest option label decides; a string or date is sized from
      // its widest value in the fleet, so it need never ellipse either.
      const valueMin =
        field.type === "enum"
          ? sizeFor(
              (field.options ?? []).map((o) => o.label),
              `600 12px ${tokens.fontSans}`,
              CELL_PAD + CHIP_PAD + 8,
              size,
              100,
              240,
            )
          : sizeFor(
              (sizeNamesFrom ?? vehicles).map((v) =>
                formatAttr(field, v.attrs[field.name] ?? null),
              ),
              `13px ${tokens.fontSans}`,
              CELL_PAD + 8,
              size,
              90,
              260,
            );
      // The heading is set uppercase and tracked; it must fit too. Measures as
      // 0 where nothing can be measured, so the fallback above stands.
      const headerMin = sizeFor(
        [label.toUpperCase()],
        `700 11px ${tokens.fontSans}`,
        CELL_PAD + 26,
        0,
        0,
        400,
      );
      return [
        columnHelper.accessor(
          (v) => formatAttr(field, v.attrs[field.name] ?? null),
          {
            id: field.name,
            header: label,
            size: Math.max(valueMin, headerMin),
            meta: { help: field.help ?? field.label },
            cell: (info) => (
              <ValueChip
                field={field}
                value={info.row.original.attrs[field.name] ?? null}
                plain={field.type === "text"}
              />
            ),
          },
        ),
      ];
    });

    return [
      identity,
      state,
      ...(production ? [production] : []),
      capability,
      tickets,
      ...configColumns,
    ];
    // `vehicles` is read only as the fallback for sizeNamesFrom; the
    // column set must not rebuild on every filter.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [schema, nameMode, now, onOpen, sizeNamesFrom]);

  /**
   * The fleet, re-identified whenever a column's *meaning* changes.
   *
   * TanStack caches each accessor's result on the row object, and rebuilds those
   * rows only when `data` changes identity — columns are not part of that memo's
   * key. The Tractor column's accessor closes over `nameMode`, so flipping the
   * toggle installed a new accessor that was never consulted: the rows served
   * the name they had cached, and the header toggle moved while the column under
   * it did not. Sorting had the same problem, one layer down, because the sorted
   * row model is keyed on the pre-sorted rows.
   */
  const data = useMemo(
    () => vehicles.slice(),
    // nameMode is not read here, and that is exactly the point: this memo exists
    // to change the array's *identity*, which is TanStack's only signal to
    // rebuild rows and drop their cached accessor values.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [vehicles, nameMode],
  );

  /**
   * Which columns, and how wide — from the box's measured width, the columns'
   * measured minimums, and whatever the reader has pinned. TanStack is handed
   * the result as its sizing state, so `getSize()`, the resize handles and the
   * rendered <col>s all say the same number.
   */
  const specs = useMemo<ColumnSpec[]>(
    () =>
      columns.map((c) => {
        const id = c.id as string;
        const configField = COLUMN_FIELDS.find((f) => f.name === id);
        return {
          id,
          min: c.size ?? MIN_SIZE,
          grow: GROW[id] ?? 1,
          core: !configField,
          priority: configField?.priority,
          defaultVisible: configField?.defaultVisible,
        };
      }),
    [columns],
  );
  const layout = useMemo(
    () => planColumns(specs, boxWidth, pinned),
    [specs, boxWidth, pinned],
  );
  const columnVisibility = useMemo(() => {
    const visibility: VisibilityState = {};
    for (const s of specs) visibility[s.id] = layout.visible.includes(s.id);
    return visibility;
  }, [specs, layout.visible]);

  /**
   * A resize (drag, or `resetSize` from a double-click / Enter) arrives as the
   * whole sizing state. The column that differs from the layout is the one the
   * reader touched: pin it, or unpin it when it has been removed. Nothing else
   * is stored, so the other columns keep following the box.
   */
  const onColumnSizingChange = useCallback(
    (
      updater:
        | ColumnSizingState
        | ((old: ColumnSizingState) => ColumnSizingState),
    ) => {
      const next =
        typeof updater === "function" ? updater(layout.widths) : updater;
      setPinned((prev) => {
        const out = { ...prev };
        for (const id of Object.keys(layout.widths)) {
          if (!(id in next)) delete out[id];
          else if (next[id] !== layout.widths[id]) out[id] = next[id];
        }
        return out;
      });
    },
    [layout.widths],
  );

  const table = useReactTable({
    data,
    columns,
    defaultColumn: { minSize: MIN_SIZE, maxSize: MAX_SIZE },
    state: {
      sorting,
      columnSizing: layout.widths,
      columnVisibility,
      expanded,
    },
    onSortingChange: setSorting,
    onColumnSizingChange,
    onExpandedChange: setExpanded,
    // Every row can open; there are no sub-rows, so this is a per-row flag.
    getRowCanExpand: () => true,
    // Keyed on the roster name rather than the row index, so an expanded row
    // stays with its tractor when the fleet is re-sorted or filtered.
    getRowId: (row) => row.vehicleName,
    enableColumnResizing: true,
    // Live feedback while dragging. The row body is memoised below so a drag
    // re-renders the <col> elements rather than every cell in the fleet.
    columnResizeMode: "onChange",
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getExpandedRowModel: getExpandedRowModel(),
  });

  /**
   * Arrow-key resizing, so the columns are usable without a mouse.
   *
   * Each step is measured from the *pending* width rather than the rendered one.
   * Holding an arrow key repeats faster than React commits, and reading the
   * committed size meant every repeat in a batch started from the same number
   * and collapsed to a single step. `fallback` is the column's configured
   * default, which is what the width is until the reader overrides it.
   */
  const nudge = useCallback(
    (
      columnId: string,
      fallback: number,
      direction: -1 | 1,
      coarse: boolean,
    ) => {
      setPinned((prev) => {
        const from = prev[columnId] ?? fallback;
        const next = from + direction * (coarse ? NUDGE_COARSE : NUDGE);
        return {
          ...prev,
          [columnId]: Math.min(MAX_SIZE, Math.max(MIN_SIZE, next)),
        };
      });
    },
    [],
  );

  const rows = table.getRowModel().rows;
  const leafColumns = table.getVisibleLeafColumns();
  const spannedColumns = leafColumns.length;
  // The config fields that have a column *right now*, so the expansion shows
  // exactly the rest: a field promoted at this width leaves the panel, and
  // comes back to it when the window narrows.
  const columnFieldNames = useMemo(
    () =>
      layout.visible.filter((id) => COLUMN_FIELDS.some((f) => f.name === id)),
    [layout.visible],
  );

  // Held stable across a resize drag: `columnSizing` changes on every mousemove,
  // and re-rendering a full fleet of cells for each one is what makes a resize
  // feel like it is snapping rather than following the cursor. The <colgroup>
  // below is what actually moves.
  //
  // `columns` has to be a dependency, not just `rows`. Cell *content* is
  // produced by the column defs — the name column renders whichever name the
  // toggle selected — and TanStack's row model is memoised on the data, so
  // switching the name toggle leaves `rows` pointing at the same array.
  const body = useMemo(
    () => (
      <tbody>
        {rows.length === 0 ? (
          <tr>
            <NoMatch colSpan={spannedColumns}>
              {query ? (
                <>
                  No tractors match “{query}”.
                  {onClearQuery && (
                    <ClearButton type="button" onClick={onClearQuery}>
                      Clear search
                    </ClearButton>
                  )}
                </>
              ) : (
                "No tractors to show."
              )}
            </NoMatch>
          </tr>
        ) : (
          rows.map((row, i) => (
            <React.Fragment key={row.id}>
              <Row
                $open={row.getIsExpanded()}
                // Banded on rendered position, not `row.index` — that is the
                // index in the source data, so after a sort the stripes would
                // arrive in whatever order the fleet happened to be stored in.
                $band={i % 2 === 1}
                // A mouse convenience only. The keyboard path is the name
                // link, which carries the row's name and focus ring.
                onClick={() => onOpen(row.original.vehicleName)}
              >
                {row.getVisibleCells().map((cell, idx) => {
                  const title = HOVER_OWNED_COLUMNS.has(cell.column.id)
                    ? undefined
                    : String(cell.getValue() ?? "");
                  const content = flexRender(
                    cell.column.columnDef.cell,
                    cell.getContext(),
                  );
                  return idx === 0 ? (
                    <NameCell key={cell.id}>{content}</NameCell>
                  ) : (
                    <Td key={cell.id} title={title}>
                      {content}
                    </Td>
                  );
                })}
              </Row>
              {row.getIsExpanded() && schema && (
                <tr id={`row-detail-${row.original.vehicleName}`}>
                  <DetailCell colSpan={spannedColumns}>
                    <RowDetail
                      vehicle={row.original}
                      schema={schema}
                      columnFields={columnFieldNames}
                      now={now}
                    />
                  </DetailCell>
                </tr>
              )}
            </React.Fragment>
          ))
        )}
      </tbody>
    ),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      rows,
      columns,
      spannedColumns,
      onOpen,
      expanded,
      schema,
      columnFieldNames,
      now,
      query,
      onClearQuery,
    ],
  );

  if (!schema) {
    return (
      <Notice>
        The field list hasn’t loaded yet, so the table can’t be drawn.
      </Notice>
    );
  }

  const resizingColumn = table.getState().columnSizingInfo.isResizingColumn;

  return (
    // One provider for the whole table: it is what makes the second tooltip in
    // a sweep open instantly instead of re-waiting the delay (see ui/tooltip).
    <TooltipProvider delayDuration={250}>
      <Scroll ref={setScrollEl}>
        <Table
          aria-label="Fleet"
          style={{ minWidth: layout.total }}
          $resizing={!!resizingColumn}
        >
          {/*
          Widths live here, not on the cells. Under `table-layout: fixed` the
          <col> widths are the whole layout, so a resize touches a handful of
          elements instead of every cell — and a column keeps its width no
          matter what the visible rows contain. They sum to the box's width
          (see tableLayout), so there is no trailing spacer to hold the slack.
        */}
          <colgroup>
            {leafColumns.map((column) => (
              <col key={column.id} style={{ width: column.getSize() }} />
            ))}
          </colgroup>
          <thead>
            {table.getHeaderGroups().map((headerGroup) => (
              <tr key={headerGroup.id}>
                {headerGroup.headers.map((header, idx) => {
                  const sorted = header.column.getIsSorted();
                  const help = (
                    header.column.columnDef.meta as
                      | { help?: string }
                      | undefined
                  )?.help;
                  const label = String(header.column.columnDef.header ?? "");
                  const resize = (e: React.KeyboardEvent) => {
                    if (e.key === "ArrowLeft" || e.key === "ArrowRight") {
                      e.preventDefault();
                      nudge(
                        header.column.id,
                        header.getSize(),
                        e.key === "ArrowLeft" ? -1 : 1,
                        e.altKey,
                      );
                      return true;
                    }
                    return false;
                  };
                  return (
                    <Th
                      key={header.id}
                      $sticky={idx === 0}
                      scope="col"
                      // Only the sorted column says how; "none" on every other
                      // header was six announcements of nothing.
                      aria-sort={
                        sorted === "asc"
                          ? "ascending"
                          : sorted === "desc"
                            ? "descending"
                            : undefined
                      }
                    >
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <SortButton
                            type="button"
                            onClick={header.column.getToggleSortingHandler()}
                            onKeyDown={(e) => {
                              if (e.shiftKey) resize(e);
                            }}
                            $active={!!sorted}
                          >
                            <HeadLabel>
                              {flexRender(
                                header.column.columnDef.header,
                                header.getContext(),
                              )}
                            </HeadLabel>
                            <SortCaret $active={!!sorted} aria-hidden>
                              {sorted === "asc"
                                ? "▲"
                                : sorted === "desc"
                                  ? "▼"
                                  : "↕"}
                            </SortCaret>
                          </SortButton>
                        </TooltipTrigger>
                        <TooltipContent side="bottom" align="start">
                          {help && <div>{help}</div>}
                          <HeaderHint $solo={!help}>
                            Click to sort · Shift+←/→ to resize
                          </HeaderHint>
                        </TooltipContent>
                      </Tooltip>
                      {/*
                      Doubles as the column rule, so the affordance is visible
                      without a hover hunt: the line between two columns *is* the
                      thing you drag. Not in the tab order — the header button
                      above resizes with the keyboard — but still a separator
                      with a value, for anything that lands on it.
                    */}
                      <Resizer
                        $active={resizingColumn === header.column.id}
                        role="separator"
                        aria-orientation="vertical"
                        aria-label={`Resize ${label} column`}
                        aria-valuenow={Math.round(header.getSize())}
                        aria-valuemin={MIN_SIZE}
                        aria-valuemax={MAX_SIZE}
                        tabIndex={-1}
                        onMouseDown={header.getResizeHandler()}
                        onTouchStart={header.getResizeHandler()}
                        onDoubleClick={() => header.column.resetSize()}
                        onKeyDown={(e) => {
                          if (e.key === "ArrowLeft" || e.key === "ArrowRight") {
                            e.preventDefault();
                            nudge(
                              header.column.id,
                              header.getSize(),
                              e.key === "ArrowLeft" ? -1 : 1,
                              e.shiftKey,
                            );
                          } else if (e.key === "Enter") {
                            e.preventDefault();
                            header.column.resetSize();
                          }
                        }}
                      />
                    </Th>
                  );
                })}
              </tr>
            ))}
          </thead>
          {body}
        </Table>
      </Scroll>
    </TooltipProvider>
  );
};

/**
 * The Open-tickets hover card: what the chips summarize, one level down.
 *
 * Titles render only for tickets the client actually holds. The fleet route
 * deliberately sends counts and never tickets (see TractorState), so against
 * live data this card shows the severity lines and says where the titles are —
 * and grows the real list for any tractor whose page has been visited, since
 * `keepDetail` holds those tickets. Snapshot mode carries every ticket, so
 * isolated dev always shows titles; don't read that as what beta will show.
 *
 * Rated tickets only, worst first, capped. The unrated majority is noise by
 * definition here — no operational status — and a card that scrolls is a page.
 */
const PREVIEW_CAP = 6;

const TicketPreview: React.FC<{ vehicle: Vehicle }> = ({ vehicle }) => {
  const breakdown = opBreakdown(vehicle);
  const unrated = unclassifiedCount(vehicle);
  const ratedCount = vehicle.openTicketCount - unrated;
  const rated = vehicle.tickets.filter((t) => t.op !== null);
  const shown = rated.slice(0, PREVIEW_CAP);

  return (
    <>
      <CardTitle>
        {vehicle.openTicketCount} open ticket
        {vehicle.openTicketCount === 1 ? "" : "s"}
      </CardTitle>
      {/* No severity legend above the list: every line names its own severity
          in full, so a summary row on top read as extra tickets — counts
          floating under the header as though something was missing from the
          list below them. The legend appears only when there is no list for
          the lines to explain themselves in. */}
      {shown.length > 0 ? (
        <TicketList>
          {shown.map((t) => {
            const oc = t.op ? OP_CONFIG[t.op] : NEUTRAL_OP;
            return (
              <TicketLine key={t.key}>
                <SeverityChip
                  style={{
                    background: oc.bg,
                    color: oc.color,
                    borderColor: oc.border,
                  }}
                >
                  {t.op ? t.op.toLowerCase() : "—"}
                </SeverityChip>
                <TicketKey>{t.key}</TicketKey>
                <TicketSummary>{t.summary || "(no title)"}</TicketSummary>
              </TicketLine>
            );
          })}
        </TicketList>
      ) : (
        ratedCount > 0 && (
          <>
            <CardSeverities>
              {breakdown.map(({ op, count, known }) => {
                const oc = known ? OP_CONFIG[op as OpStatus] : NEUTRAL_OP;
                return (
                  <SeverityChip
                    key={op}
                    style={{
                      background: oc.bg,
                      color: oc.color,
                      borderColor: oc.border,
                    }}
                  >
                    {count} {op.toLowerCase()}
                  </SeverityChip>
                );
              })}
            </CardSeverities>
            <CardNote>Open the tractor for ticket titles.</CardNote>
          </>
        )
      )}
      {rated.length > shown.length && (
        <CardNote>
          +{rated.length - shown.length} more rated — the tractor page has the
          full list.
        </CardNote>
      )}
      {unrated > 0 && (
        <CardNote>
          {ratedCount > 0 ? `+${unrated}` : unrated} with no operational status
          set in Jira.
        </CardNote>
      )}
    </>
  );
};

/**
 * The scroll box.
 *
 * `flex: 1` + `min-height: 0` inside FleetView's column, so it occupies exactly
 * the space the masthead above it did not — which is what makes the sticky
 * header pin (sticky needs a bounded scroll container) without anyone having to
 * guess how tall the chrome above the table is.
 *
 * Below the shell's breakpoint the page scrolls with the window and this box has
 * no definite height to fill, so it falls back to a viewport cap — still
 * bounded, so the header still pins.
 */
const Scroll = styled.div`
  flex: 1;
  min-height: 0;
  overflow: auto;
  /* Reserved whether or not the rows overflow, so filtering the fleet down to
     a few rows does not widen every column by a scrollbar. */
  scrollbar-gutter: stable;
  overscroll-behavior: contain;
  background: ${tokens.white};
  border: 1px solid ${tokens.line};
  border-radius: 14px;
  box-shadow: ${tokens.shadowSm};

  @media (max-width: 1032px) {
    max-height: calc(100vh - 180px);
  }
`;

const Table = styled.table<{ $resizing?: boolean }>`
  border-collapse: separate;
  border-spacing: 0;
  table-layout: fixed;
  width: 100%;
  font-size: 13px;
  /* A drag that selects the header text instead of moving the column reads as
     the table fighting back. */
  user-select: ${(p) => (p.$resizing ? "none" : "auto")};
`;

const cell = `
  padding: 9px 12px;
  text-align: left;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const Th = styled.th<{ $sticky?: boolean }>`
  ${cell}
  position: sticky;
  top: 0;
  left: ${(p) => (p.$sticky ? "0" : "auto")};
  z-index: ${(p) => (p.$sticky ? 4 : 3)};
  background: ${tokens.offWhite};
  border-bottom: 1px solid ${tokens.line};
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: ${tokens.navy};
  /* Overflow has to stay visible for the resize handle to sit in the padding
     gutter; the label clips itself instead (see HeadLabel). */
  overflow: visible;
`;

// The header label is the sort control: a full-width button so the whole cell
// is a hit target, with a caret that reads as sortable when idle and as the
// active direction once sorted.
const SortButton = styled.button<{ $active?: boolean }>`
  font: inherit;
  color: ${(p) => (p.$active ? tokens.blue : "inherit")};
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 6px;
  width: 100%;
  padding: 0;
  border: none;
  background: none;
  cursor: pointer;
  text-align: left;
  user-select: none;

  &:hover {
    color: ${tokens.blue};
  }
  &:focus-visible {
    outline: 2px solid ${tokens.blue};
    outline-offset: 2px;
  }
`;

/** Clips the header text, since the <th> itself must not (see Th). */
const HeadLabel = styled.span`
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const SortCaret = styled.span<{ $active?: boolean }>`
  flex-shrink: 0;
  font-size: 10px;
  line-height: 1;
  opacity: ${(p) => (p.$active ? 1 : 0.35)};
`;

/**
 * The drag target between two columns.
 *
 * Wider than the rule it draws: a 1px line is an unfair thing to ask someone to
 * hit, so the hit area spans the cell's right gutter and the visible rule sits
 * inside it. Not a tab stop (see the module note), but focusable by script and
 * still answers arrow keys.
 */
const Resizer = styled.div<{ $active?: boolean }>`
  position: absolute;
  top: 0;
  right: 0;
  height: 100%;
  width: 11px;
  cursor: col-resize;
  user-select: none;
  touch-action: none;
  z-index: 1;

  &::after {
    content: "";
    position: absolute;
    top: 6px;
    bottom: 6px;
    right: 5px;
    width: ${(p) => (p.$active ? "2px" : "1px")};
    background: ${(p) => (p.$active ? tokens.blue : tokens.line)};
  }

  &:hover::after {
    width: 2px;
    background: ${tokens.blue};
  }

  &:focus-visible {
    outline: 2px solid ${tokens.blue};
    outline-offset: -2px;
  }
`;

const Td = styled.td`
  ${cell}
  border-bottom: 1px solid ${tokens.line};
  vertical-align: middle;
`;

const NameInner = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  max-width: 100%;
  overflow: hidden;
`;

/** The disclosure control. Distinct from the row, which opens the tractor. */
const Chevron = styled.button`
  flex-shrink: 0;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 18px;
  height: 18px;
  padding: 0;
  border: none;
  border-radius: 4px;
  background: none;
  color: ${tokens.slate};
  cursor: pointer;

  &:hover {
    background: ${tokens.line};
    color: ${tokens.ink};
  }
  &:focus-visible {
    outline: 2px solid ${tokens.blue};
    outline-offset: 1px;
  }
`;

const Caret = styled.span<{ $open?: boolean }>`
  font-size: 10px;
  line-height: 1;
  transition: transform 0.12s ease;
  transform: rotate(${(p) => (p.$open ? "90deg" : "0deg")});

  @media (prefers-reduced-motion: reduce) {
    transition: none;
  }
`;

const NameLink = styled.a`
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  color: ${tokens.ink};
  text-decoration: none;
  border-radius: 3px;

  &:hover {
    color: ${tokens.blue};
    text-decoration: none;
  }
  &:focus-visible {
    outline: 2px solid ${tokens.blue};
    outline-offset: 2px;
  }
`;

const TicketCell = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  max-width: 100%;
  overflow: hidden;
  padding: 0;
  border: none;
  background: none;
  font: inherit;
  color: inherit;
  text-align: left;
  cursor: pointer;
  border-radius: 4px;

  &:focus-visible {
    outline: 2px solid ${tokens.blue};
    outline-offset: 2px;
  }
`;

const TicketTotal = styled.span`
  flex-shrink: 0;
  min-width: 18px;
  font-variant-numeric: tabular-nums;
  font-weight: 700;
  color: ${tokens.ink};
`;

/** Zero is the quiet answer, so it reads as one rather than as a score. Quiet,
 *  not invisible: the lighter grey sat at 1.6:1 against the row. */
const Quiet = styled.span`
  font-variant-numeric: tabular-nums;
  color: ${tokens.slate};
`;

/**
 * One severity, on its own hue, with its name. The cell shows "2 crit"; the
 * hover card shows the full word. Colour reinforces; the word carries.
 */
const SeverityChip = styled.span`
  flex-shrink: 0;
  font-size: 11px;
  font-weight: 600;
  font-variant-numeric: tabular-nums;
  letter-spacing: 0.01em;
  border: 1px solid;
  border-radius: 5px;
  padding: 1px 5px;
  white-space: nowrap;
`;

/**
 * Open tickets with no operational status set in Jira. Deliberately not
 * chip-shaped: "unclassified" is the absence of a severity, not a level of one,
 * and a fourth grey pill would read as the quiet end of the scale.
 */
const Unrated = styled.span`
  flex-shrink: 0;
  font-size: 11.5px;
  color: ${tokens.slate};
`;

// --- Hover-card interiors -----------------------------------------------
// The shells (border, shadow, type) come from ui/hover-card and ui/tooltip;
// these lay out what goes inside them, in the same tokens as the cells.

const CardTitle = styled.div`
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: ${tokens.slate};
  margin-bottom: 8px;
`;

const TicketList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 5px;
`;

const TicketLine = styled.div`
  display: flex;
  align-items: baseline;
  gap: 6px;
  min-width: 0;
`;

const TicketKey = styled.span`
  flex-shrink: 0;
  font-family: ${tokens.fontMono};
  font-size: 11px;
  color: ${tokens.slate};
`;

/** One line, ellipsed — the preview shows what a ticket is, not its prose. */
const TicketSummary = styled.span`
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  color: ${tokens.ink};
`;

/** The severity legend — which name belongs to each of the cell's hues. */
const CardSeverities = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  margin-bottom: 6px;
`;

const CardNote = styled.div`
  margin-top: 8px;
  font-size: 11.5px;
  color: ${tokens.slate};
`;

/** The header tooltip's sort hint — muted under a help sentence, alone otherwise. */
const HeaderHint = styled.div<{ $solo?: boolean }>`
  color: ${(p) => (p.$solo ? "inherit" : tokens.slate)};
  font-size: ${(p) => (p.$solo ? "inherit" : "11px")};
  margin-top: ${(p) => (p.$solo ? "0" : "3px")};
`;

const NameCell = styled.td`
  ${cell}
  position: sticky;
  left: 0;
  z-index: 2;
  background: inherit;
  border-bottom: 1px solid ${tokens.line};
  font-weight: 600;
`;

/**
 * Row banding, in three states that have to stay distinguishable.
 *
 * Wide rows with a lot of white in them lose their horizontal thread — the eye
 * slides a line while crossing from the name to the version. The band is
 * deliberately faint: it only has to be enough to follow, and a stripe strong
 * enough to notice on its own competes with the severity colours the cells are
 * carrying, which are the part meant to be noticed.
 *
 * Hover has to beat the band, and an expanded row has to beat hover — it stays
 * lit while the cursor moves down into its panel, which is the only cue tying a
 * detached block of detail to the row that opened it.
 */
const Row = styled.tr<{ $open?: boolean; $band?: boolean }>`
  background: ${(p) => (p.$open ? ROW.open : p.$band ? ROW.band : ROW.plain)};
  cursor: pointer;

  &:hover {
    background: ${(p) => (p.$open ? ROW.openHover : ROW.hover)};
  }
`;

/**
 * The expansion's cell. It spans every column, so there is nothing for the
 * pinned first column to sit over and no sticky behaviour to arrange — the
 * panel is simply as wide as the table.
 */
const DetailCell = styled.td`
  padding: 0;
  border-bottom: 1px solid ${tokens.line};
  /* Same tint as the row that opened it, so the panel reads as that row's
     continuation rather than as a block that happens to be underneath it. */
  background: ${ROW.open};
  box-shadow: inset 3px 0 0 ${tokens.blue};
`;

const NoMatch = styled.td`
  padding: 20px 12px;
  text-align: center;
  font-size: 13px;
  color: ${tokens.slate};
`;

const ClearButton = styled.button`
  margin-left: 10px;
  padding: 3px 12px;
  border-radius: 999px;
  border: 1px solid ${tokens.line};
  background: ${tokens.white};
  font: inherit;
  font-weight: 600;
  color: ${tokens.ink};
  cursor: pointer;

  &:hover {
    border-color: ${tokens.blue};
    color: ${tokens.blue};
  }
  &:focus-visible {
    outline: 2px solid ${tokens.blue};
    outline-offset: 2px;
  }
`;

const Notice = styled.div`
  /* Stands in for Scroll as FleetView's second child, but sized by its text
     rather than claiming the leftover height for one sentence. */
  flex: none;
  padding: 20px;
  font-size: 13px;
  color: ${tokens.slate};
  background: ${tokens.white};
  border: 1px solid ${tokens.line};
  border-radius: 14px;
`;

/** The Production cell of a tractor nobody has marked. */
const Unmarked = styled.span`
  color: ${tokens.slate};
`;

export default VehicleTable;
